// Anexos activos — la superficie donde aterriza el operador de Desarrollos.
//
// Solo lectura. Cola de todos los Anexos en estado 'live' del tenant, del más
// recientemente actualizado al más viejo, y una suscripción realtime para que
// la laptop del operador se actualice sola cuando un cliente captura algo.

import { useCallback, useEffect, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { DESARROLLOS_TENANT_ID } from '../tenant';
import AnexoQueueCard from './AnexoQueueCard';

const ROLES_PERMITIDOS = ['admin', 'manager']; // 'bodega' se agrega post-expo

const SkeletonCard = () => (
  <div className="bg-white border border-slate-200 rounded-2xl p-5" aria-hidden="true">
    <div className="h-5 w-40 rounded bg-slate-100 animate-pulse" />
    <div className="mt-2 h-4 w-20 rounded bg-slate-100 animate-pulse" />
    <div className="mt-5 h-3 w-32 rounded bg-slate-100 animate-pulse" />
    <div className="mt-4 h-11 rounded-lg bg-slate-100 animate-pulse" />
  </div>
);

// El embed de PostgREST es la ruta rápida; si el cache de esquema no resuelve
// alguna relación, caemos a tres lecturas planas y unimos en el cliente.
const fetchQueue = async () => {
  const embedded = await supabase
    .from('desarrollos_anexos')
    .select(
      `
      id,
      client_id,
      status,
      created_at,
      updated_at,
      customer:customers ( full_name, phone_number ),
      intake_events:desarrollos_intake_events ( count ),
      items:desarrollos_anexo_items ( count )
    `
    )
    .eq('tenant_id', DESARROLLOS_TENANT_ID)
    .eq('status', 'live')
    .order('updated_at', { ascending: false });

  if (!embedded.error && embedded.data) {
    const rows = embedded.data;
    const embedsResolved =
      rows.length === 0 || rows.every((r) => r.customer !== undefined);
    if (embedsResolved) {
      return rows.map((r) => ({
        id: r.id,
        client_id: r.client_id,
        updated_at: r.updated_at,
        full_name: r.customer?.full_name || null,
        phone_number: r.customer?.phone_number || null,
        intake_count: r.intake_events?.[0]?.count ?? 0,
        item_count: r.items?.[0]?.count ?? 0,
      }));
    }
  }

  if (embedded.error) {
    console.warn('[Desarrollos/cola] embed falló, usando lecturas planas', embedded.error);
  }

  // ── Fallback plano ──
  const { data: anexos, error } = await supabase
    .from('desarrollos_anexos')
    .select('id, client_id, updated_at')
    .eq('tenant_id', DESARROLLOS_TENANT_ID)
    .eq('status', 'live')
    .order('updated_at', { ascending: false });
  if (error) throw error;
  if (!anexos || anexos.length === 0) return [];

  const ids = anexos.map((a) => a.id);
  const [customers, intake, items] = await Promise.all([
    supabase
      .from('customers')
      .select('id, full_name, phone_number')
      .eq('tenant_id', DESARROLLOS_TENANT_ID)
      .in('id', anexos.map((a) => a.client_id)),
    supabase
      .from('desarrollos_intake_events')
      .select('anexo_id')
      .eq('tenant_id', DESARROLLOS_TENANT_ID)
      .in('anexo_id', ids),
    supabase
      .from('desarrollos_anexo_items')
      .select('anexo_id')
      .eq('tenant_id', DESARROLLOS_TENANT_ID)
      .in('anexo_id', ids),
  ]);
  if (customers.error) throw customers.error;
  if (intake.error) throw intake.error;
  if (items.error) throw items.error;

  const byClient = Object.fromEntries((customers.data || []).map((c) => [c.id, c]));
  const tally = (rows) => {
    const out = {};
    (rows || []).forEach((r) => {
      out[r.anexo_id] = (out[r.anexo_id] || 0) + 1;
    });
    return out;
  };
  const intakeCount = tally(intake.data);
  const itemCount = tally(items.data);

  return anexos.map((a) => ({
    id: a.id,
    client_id: a.client_id,
    updated_at: a.updated_at,
    full_name: byClient[a.client_id]?.full_name || null,
    phone_number: byClient[a.client_id]?.phone_number || null,
    intake_count: intakeCount[a.id] || 0,
    item_count: itemCount[a.id] || 0,
  }));
};

const DesarrollosActivosQueue = ({ onOpenAnexo, onCountChange, onDenied }) => {
  const [gate, setGate] = useState('checking'); // checking | denied | ok
  const [anexos, setAnexos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const refetchQueue = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) setRefreshing(true);
      try {
        const rows = await fetchQueue();
        if (!mounted.current) return;
        setAnexos(rows);
        setError('');
        if (onCountChange) onCountChange(rows.length);
      } catch (err) {
        console.error('[Desarrollos/cola] error cargando los Anexos', err);
        if (mounted.current) {
          setError('No pudimos cargar la cola. Revisá tu conexión y volvé a intentar.');
        }
      } finally {
        if (mounted.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [onCountChange]
  );

  // ── Gates: sesión → rol → cola ───────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      const session = sessionData?.session;
      if (!session) {
        if (!cancelled && onDenied) onDenied();
        return;
      }

      const { data: staff, error: staffError } = await supabase
        .from('staff_members')
        .select('role, tenant_id')
        .eq('id', session.user.id)
        .maybeSingle();

      if (cancelled) return;

      const permitido =
        !staffError &&
        staff &&
        ROLES_PERMITIDOS.includes(staff.role) &&
        staff.tenant_id === DESARROLLOS_TENANT_ID;

      if (!permitido) {
        setGate('denied');
        setLoading(false);
        return;
      }

      setGate('ok');
      refetchQueue({ silent: true });
    })();

    return () => {
      cancelled = true;
    };
  }, [refetchQueue, onDenied]);

  // ── Realtime ─────────────────────────────────────────────────────────────
  // Se escuchan DOS tablas a propósito. desarrollos_anexos cubre la creación
  // del Anexo (primera captura del cliente). Pero las capturas siguientes solo
  // insertan en desarrollos_intake_events y NO tocan la fila del Anexo — no hay
  // trigger que le mueva updated_at — así que sin esta segunda suscripción los
  // conteos de la tarjeta se quedarían viejos justo durante la demo.
  useEffect(() => {
    if (gate !== 'ok') return undefined;

    const channel = supabase
      .channel('desarrollos_anexos_queue')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'desarrollos_anexos',
          filter: `tenant_id=eq.${DESARROLLOS_TENANT_ID}`,
        },
        () => refetchQueue({ silent: true })
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'desarrollos_intake_events',
          filter: `tenant_id=eq.${DESARROLLOS_TENANT_ID}`,
        },
        () => refetchQueue({ silent: true })
      )
      .subscribe((status) => {
        // Deja rastro en consola: si las tablas no están en la publicación
        // supabase_realtime, el canal se suscribe igual y nunca llega un evento.
        console.log('[Desarrollos/cola] estado del canal realtime:', status);
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [gate, refetchQueue]);

  if (gate === 'denied') {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center">
        <p className="text-sm font-bold text-slate-900">Sin permisos para Desarrollos</p>
        <p className="mt-2 text-xs text-slate-500">
          Tu usuario no tiene rol de operador en este tenant.
        </p>
        <button
          type="button"
          onClick={onDenied}
          className="mt-5 min-h-[44px] px-4 text-xs font-bold text-slate-600 hover:text-slate-900 transition-colors"
        >
          Volver al inicio de sesión
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2.5">
          <h2 className="text-lg font-bold text-slate-900">Anexos activos</h2>
          <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
            {loading ? '—' : anexos.length}
          </span>
        </div>
        <button
          type="button"
          onClick={() => refetchQueue()}
          disabled={refreshing || loading}
          className="min-h-[44px] px-4 flex items-center gap-2 border border-slate-200 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-60 transition-colors"
        >
          <RefreshCw size={14} className={refreshing ? 'animate-spin' : undefined} />
          Actualizar
        </button>
      </div>

      {error && <p className="mb-4 text-xs text-red-600">{error}</p>}

      {loading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : anexos.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-10">
          <p className="text-sm font-bold text-slate-900">Aún no hay Anexos activos</p>
          <p className="mt-2 text-xs text-slate-500 leading-relaxed">
            Cuando un cliente comience a enviar su información, aparecerá aquí.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {anexos.map((anexo) => (
            <AnexoQueueCard key={anexo.id} anexo={anexo} onOpen={onOpenAnexo} />
          ))}
        </div>
      )}
    </>
  );
};

export default DesarrollosActivosQueue;
